import styled, { createGlobalStyle, keyframes } from 'styled-components'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0,229,160, .35); }
  50%      { box-shadow: 0 0 0 12px rgba(0,229,160, 0); }
`
const flashOk = keyframes`
  0%   { background: rgba(0,229,160,.18); }
  100% { background: transparent; }
`
const flashErr = keyframes`
  0%   { background: rgba(239,68,68,.18); }
  100% { background: transparent; }
`

const c = {
  bg: '#0a0c0f', bg2: '#111318', bg3: '#181c23', bg4: '#1e2330',
  bdr: '#1f2430', bdr2: '#2a3040',
  acc: '#00e5a0', acc2: '#00b87a',
  adim: 'rgba(0,229,160,.08)', adim2: 'rgba(0,229,160,.18)',
  txt: '#e8ecf0', txt2: '#8a95a3', txt3: '#5a6370',
  warn: '#f59e0b', danger: '#ef4444', info: '#3b82f6',
  mono: "'Space Mono',monospace", sans: "'DM Sans',sans-serif",
}

export const GlobalEmbarkStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
`

export const PageWrap = styled.div`
  background:${c.bg}; color:${c.txt}; font-family:${c.sans};
  min-height:100vh; display:flex; flex-direction:column;
  /* Em telas pequenas (mobile/tablet) o conteúdo precisa rolar — não trava no viewport */
  @media (max-width: 900px) { height:auto; overflow:visible; }
  @media (min-width: 901px) { height:100vh; overflow:hidden; }
`

export const Topbar = styled.div`
  background:${c.bg2}; border-bottom:1px solid ${c.bdr};
  padding:10px 16px; min-height:48px;
  display:flex; align-items:center; gap:10px; flex-shrink:0; flex-wrap:wrap;
  h2 { font-size:13px; font-weight:600; color:${c.txt}; }
  .crumb { font-size:11px; color:${c.txt3}; }
  @media (max-width: 600px) {
    padding:10px 12px;
    h2 { font-size:12px; }
    .crumb { display:none; }
  }
`

export const ReEmbarkBtn = styled.button`
  margin-left:auto;
  background:transparent; border:1px solid ${c.warn}; color:${c.warn};
  padding:8px 14px; border-radius:7px; cursor:pointer;
  font-size:11px; font-weight:600; font-family:${c.sans};
  transition:.15s; display:inline-flex; align-items:center; gap:6px;
  &:hover { background:rgba(245,158,11,.1); }
  @media (max-width: 600px) { padding:7px 10px; font-size:10px; }
`

export const Body = styled.div`
  flex:1; display:grid; grid-template-columns: 1fr 380px;
  min-height:0;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    overflow:visible;
  }
  @media (min-width: 901px) { overflow:hidden; }
`

// ─── Coluna principal: tela de bipe ────────────────────────────────────────
export const MainCol = styled.div`
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:40px 30px; overflow-y:auto;
  background: radial-gradient(circle at center 35%, ${c.bg2}, ${c.bg} 70%);
  @media (max-width: 900px) { padding:24px 14px; justify-content:flex-start; }
  @media (max-width: 600px) { padding:18px 10px; }
`
export const ScanCard = styled.div`
  width:100%; max-width:560px;
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:14px;
  padding:30px 32px; text-align:center;
  box-shadow:0 12px 40px rgba(0,0,0,.4);
  animation:${pulse} 2.4s ease-in-out infinite;
  @media (max-width: 600px) { padding:20px 16px; border-radius:12px; }
`
export const ScanIcon = styled.div`
  font-size:48px; margin-bottom:10px;
  @media (max-width: 600px) { font-size:38px; }
`
export const ScanTitle = styled.h2`
  font-size:18px; font-weight:600; color:${c.txt}; margin-bottom:6px;
  @media (max-width: 600px) { font-size:16px; }
`
export const ScanHint = styled.p`
  font-size:12px; color:${c.txt3}; margin-bottom:22px; line-height:1.5;
`
export const ScanInput = styled.input`
  width:100%; background:${c.bg3}; border:2px solid ${c.bdr2};
  border-radius:10px; padding:16px 18px;
  color:${c.txt}; font-size:18px; font-family:${c.mono};
  outline:none; text-align:center; letter-spacing:.04em;
  transition:.15s;
  &:focus { border-color:${c.acc}; box-shadow:0 0 0 4px ${c.adim}; }
  &::placeholder { color:${c.txt3}; letter-spacing:.02em; }
  /* Mobile: input maior pro toque + impedir zoom do iOS (font-size >= 16px) */
  @media (max-width: 600px) { padding:18px 14px; font-size:17px; }
`
export const ScanFeedback = styled.div`
  margin-top:18px; min-height:24px;
  font-size:12px; font-family:${c.sans};
  color:${({ $kind }) => $kind === 'ok' ? c.acc : $kind === 'err' ? c.danger : c.txt3};
  font-weight:500;
`

// ─── Cards de resultado (último embarque) ──────────────────────────────────
export const ResultCard = styled.div`
  margin-top:22px; width:100%; max-width:560px;
  background:${c.bg2}; border:1px solid ${c.bdr};
  border-left:4px solid ${({ $kind }) => $kind === 'ok' ? c.acc : c.danger};
  border-radius:10px; padding:16px 18px;
  animation:${fadeIn} .2s ease;
  display:flex; align-items:center; gap:14px;
  .icon { font-size:24px; flex-shrink:0; }
  .info { flex:1; }
  .nf {
    font-family:${c.mono}; font-size:16px; font-weight:700;
    color:${({ $kind }) => $kind === 'ok' ? c.acc : c.danger};
  }
  .name { font-size:12px; color:${c.txt2}; margin-top:2px; }
  .msg { font-size:11px; color:${c.txt3}; margin-top:4px; }
`

// ─── Coluna lateral: histórico da sessão ───────────────────────────────────
export const SideCol = styled.aside`
  background:${c.bg2}; border-left:1px solid ${c.bdr};
  display:flex; flex-direction:column;
  overflow:hidden;
  @media (max-width: 900px) {
    border-left:none; border-top:1px solid ${c.bdr};
    max-height:none; overflow:visible;
  }
`
export const SideHeader = styled.div`
  padding:14px 18px; border-bottom:1px solid ${c.bdr};
  h3 { font-size:13px; font-weight:600; color:${c.txt}; margin-bottom:3px; }
  p { font-size:11px; color:${c.txt3}; }
`
export const SessionStats = styled.div`
  padding:12px 18px; border-bottom:1px solid ${c.bdr};
  display:grid; grid-template-columns:1fr 1fr; gap:10px;
  background:${c.bg3};
`
export const SessionStat = styled.div`
  .l { font-size:10px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.05em; }
  .v {
    font-family:${c.mono}; font-size:18px; font-weight:700;
    color:${({ $variant }) => $variant === 'ok' ? c.acc : $variant === 'err' ? c.danger : c.txt};
  }
`
export const HistoryList = styled.div`
  flex:1; overflow-y:auto; padding:8px;
  &::-webkit-scrollbar { width:4px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:2px; }
  @media (max-width: 900px) { max-height:280px; }
`
export const HistoryEmpty = styled.div`
  padding:30px 20px; text-align:center; color:${c.txt3}; font-size:11px;
  line-height:1.6;
`
export const HistoryItem = styled.div`
  background:${c.bg3}; border:1px solid ${c.bdr};
  border-left:3px solid ${({ $kind }) => $kind === 'ok' ? c.acc : c.danger};
  border-radius:8px; padding:9px 12px; margin-bottom:5px;
  animation:${fadeIn} .15s ease;
  display:flex; gap:10px; align-items:center;
  .icon { font-size:14px; flex-shrink:0;
          color:${({ $kind }) => $kind === 'ok' ? c.acc : c.danger}; }
  .info { flex:1; min-width:0; }
  .nf {
    font-family:${c.mono}; font-size:11px; font-weight:700;
    color:${({ $kind }) => $kind === 'ok' ? c.acc : c.danger};
  }
  .name {
    font-size:10px; color:${c.txt2}; margin-top:1px;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  }
  .time {
    font-family:${c.mono}; font-size:9px; color:${c.txt3};
    flex-shrink:0;
  }
`

// ─── Modal Re-embarcar ─────────────────────────────────────────────────────
export const ModalOverlay = styled.div`
  position:fixed; inset:0; z-index:2000;
  background:rgba(0,0,0,.6); backdrop-filter:blur(2px);
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  align-items:center; justify-content:center;
`
export const ReBox = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:12px;
  padding:24px 28px; width:540px; max-width:calc(100vw - 24px);
  max-height:calc(100vh - 24px); overflow-y:auto;
  box-shadow:0 16px 48px rgba(0,0,0,.7);
  border-top:3px solid ${c.warn};
  h3 { font-size:15px; font-weight:600; color:${c.txt}; margin-bottom:6px; }
  p { font-size:12px; color:${c.txt3}; margin-bottom:18px; line-height:1.5; }
  @media (max-width: 600px) { padding:18px 16px; border-radius:10px; }
`
export const StepIndicator = styled.div`
  display:flex; align-items:center; gap:10px; margin-bottom:18px;
`
export const Step = styled.div`
  flex:1; display:flex; align-items:center; gap:8px;
  font-size:11px; color:${({ $active, $done }) =>
    $done ? c.acc : $active ? c.warn : c.txt3};
  font-weight:${({ $active }) => $active ? 600 : 400};
  .num {
    width:22px; height:22px; border-radius:50%;
    display:flex; align-items:center; justify-content:center;
    font-family:${c.mono}; font-size:10px; font-weight:700;
    background:${({ $active, $done }) =>
      $done ? c.acc : $active ? c.warn : c.bg3};
    color:${({ $active, $done }) =>
      $done || $active ? '#000' : c.txt3};
    flex-shrink:0;
  }
`
export const StepArrow = styled.div`
  font-size:14px; color:${c.txt3};
`
export const ReInput = styled.input`
  width:100%; background:${c.bg3}; border:2px solid ${({ $kind }) =>
    $kind === 'err' ? c.danger : $kind === 'ok' ? c.acc : c.bdr2};
  border-radius:10px; padding:14px 16px;
  color:${c.txt}; font-size:16px; font-family:${c.mono};
  outline:none; text-align:center; letter-spacing:.04em;
  transition:.15s;
  &:focus { border-color:${({ $kind }) =>
    $kind === 'err' ? c.danger : c.acc}; }
  &:disabled { opacity:.6; }
  @media (max-width: 600px) { padding:16px 12px; font-size:16px; }
`

export const SuggestionBox = styled.div`
  margin-top:14px; padding:14px;
  background:${c.adim}; border:1px solid ${c.acc2}; border-radius:9px;
  display:flex; align-items:center; gap:12px;
  animation:${fadeIn} .15s ease;
  .icon { font-size:24px; }
  .info { flex:1; }
  .lbl { font-size:10px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.06em; }
  .name {
    font-family:${c.mono}; font-size:15px; font-weight:700;
    color:${c.acc}; margin-top:2px;
  }
  .meta { font-size:10px; color:${c.txt3}; margin-top:3px; }
`

export const InfoLine = styled.div`
  margin-top:10px; font-size:11px; color:${c.txt2};
  display:flex; gap:10px; flex-wrap:wrap;
  span { background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:5px;
         padding:3px 8px; font-family:${c.mono}; font-size:10px; }
  strong { color:${c.txt}; }
`

export const ModalActions = styled.div`
  display:flex; justify-content:flex-end; gap:8px; margin-top:20px;
  @media (max-width: 600px) { flex-direction:column-reverse; }
`
export const ModalBtn = styled.button`
  padding:10px 18px; border-radius:7px; cursor:pointer;
  font-size:13px; font-weight:600; transition:.15s; font-family:${c.sans};
  background:${({ $primary }) => $primary ? c.acc : 'transparent'};
  border:1px solid ${({ $primary }) => $primary ? c.acc : c.bdr2};
  color:${({ $primary }) => $primary ? '#000' : c.txt2};
  &:hover { ${({ $primary }) => $primary ? 'filter:brightness(1.1);' : `border-color:${c.acc}; color:${c.acc};`} }
  &:disabled { opacity:.5; cursor:not-allowed; }
  @media (max-width: 600px) { width:100%; padding:14px; font-size:14px; }
`

// ─── BOX-FIRST FLOW: banner do box atual + lista de volumes ────────────────
export const BoxBanner = styled.div`
  background:${c.bg2}; border:1px solid ${c.acc2}; border-radius:12px;
  padding:16px 20px; margin-bottom:14px;
  display:grid; grid-template-columns:1fr auto; gap:14px; align-items:center;
  animation:${fadeIn} .15s ease;
  width:100%; max-width:560px;
  .info { min-width:0; }
  .lbl { font-size:10px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.06em; }
  .name { font-family:${c.mono}; font-size:22px; font-weight:700; color:${c.acc}; line-height:1.1; }
  .meta { font-size:11px; color:${c.txt2}; margin-top:4px; }
  .progress {
    font-family:${c.mono}; font-size:24px; font-weight:700;
    color:${({ $complete }) => $complete ? c.acc : c.warn};
    text-align:right; line-height:1;
  }
  .progress-lbl { font-size:9px; color:${c.txt3}; text-transform:uppercase;
                  letter-spacing:.06em; margin-top:2px; text-align:right; }
  @media (max-width: 600px) { padding:14px 16px; .name { font-size:18px; } .progress { font-size:20px; } }
`

export const VolumesPanel = styled.div`
  width:100%; max-width:560px;
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:12px;
  padding:14px 18px;
  h4 { font-size:11px; font-weight:600; color:${c.txt3};
       text-transform:uppercase; letter-spacing:.06em; margin-bottom:10px; }
`

export const VolumesList = styled.div`
  display:flex; flex-direction:column; gap:6px;
  max-height:280px; overflow-y:auto; padding-right:4px;
  &::-webkit-scrollbar { width:4px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:2px; }
`

export const VolumeRow = styled.div`
  display:grid; grid-template-columns:24px 1fr auto; gap:10px;
  align-items:center;
  background:${({ $embarked }) => $embarked ? 'rgba(0,229,160,.06)' : c.bg3};
  border:1px solid ${({ $embarked }) => $embarked ? c.acc2 : c.bdr};
  border-left:3px solid ${({ $embarked }) => $embarked ? c.acc : c.warn};
  border-radius:7px; padding:8px 12px;
  transition:.12s;
  ${({ $embarked }) => $embarked && `opacity:.65;`}
  .check { font-size:14px; text-align:center;
           color:${({ $embarked }) => $embarked ? c.acc : c.txt3}; }
  .body { min-width:0; }
  .rastreio { font-family:${c.mono}; font-size:12px; font-weight:700; color:${c.txt};
              white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .meta { font-size:10px; color:${c.txt3}; margin-top:2px;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .nf { font-family:${c.mono}; font-size:11px; color:${c.txt2}; font-weight:600; }
`

export const BulkEmbarkBtn = styled.button`
  width:100%; max-width:560px; margin-top:14px;
  background:${c.acc}; border:none; color:#000;
  padding:14px; border-radius:10px;
  font-size:14px; font-weight:700; cursor:pointer; font-family:${c.sans};
  transition:.15s;
  &:hover { filter:brightness(1.1); }
  &:disabled { opacity:.5; cursor:not-allowed; }
`

export const ResetBoxBtn = styled.button`
  width:100%; max-width:560px; margin-top:8px;
  background:transparent; border:1px solid ${c.bdr2}; color:${c.txt3};
  padding:10px; border-radius:8px;
  font-size:12px; cursor:pointer; font-family:${c.sans};
  transition:.12s;
  &:hover { border-color:${c.danger}; color:${c.danger}; }
`

export const RedirectBanner = styled.div`
  width:100%; max-width:560px; margin-top:12px;
  padding:12px 14px; border-radius:8px;
  background:rgba(245,158,11,.10); border:1px solid #f59e0b;
  display:flex; gap:10px; align-items:center;
  font-size:12px; color:#fbbf24;
  animation:${fadeIn} .15s ease;
  .icon { font-size:18px; flex-shrink:0; }
  .text { flex:1; line-height:1.4; }
  button {
    background:#f59e0b; color:#000; border:none; border-radius:6px;
    padding:7px 12px; font-size:11px; font-weight:700; cursor:pointer;
    font-family:${c.sans}; white-space:nowrap;
    &:hover { filter:brightness(1.1); }
  }
`

export const EmptyBoxState = styled.div`
  width:100%; max-width:560px; margin-top:14px;
  background:rgba(0,229,160,.08); border:1px solid ${c.acc2}; border-radius:12px;
  padding:24px; text-align:center;
  animation:${fadeIn} .15s ease;
  .icon { font-size:36px; margin-bottom:6px; }
  h4 { font-size:14px; font-weight:600; color:${c.acc}; margin-bottom:4px; }
  p { font-size:11px; color:${c.txt3}; }
`
